import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getQuotations } from "../../../../api/quotation";
import { Copier } from "../../../../components/common/Copier";
import { useApoAuth } from "../../../../hooks";
import { getTooltipClassName } from "../../../../utils";

interface QuotationReferenceTagProps {
  selectedSessionId: string | null;
  isDarkMode: boolean;
}

export function QuotationReferenceTag({
  selectedSessionId,
  isDarkMode,
}: QuotationReferenceTagProps) {
  const [token, setToken] = useState<string | null>(null);
  const { getIdTokenClaims, source_entity } = useApoAuth();

  // Get and set token
  useEffect(() => {
    getIdTokenClaims().then((data) => !!data && setToken(data.__raw));
  }, [getIdTokenClaims]);

  // Fetch quotation request by chat_session_uid
  const { data: quotationData } = useQuery({
    queryKey: ["quotationRequest", selectedSessionId, token],
    queryFn: () => {
      if (!token || !selectedSessionId || !source_entity) {
        throw new Error("Missing required parameters");
      }
      return getQuotations({
        token,
        params: { source_entity, chat_session_uid: selectedSessionId },
      });
    },
    enabled: !!token && !!selectedSessionId && !!source_entity,
    refetchOnWindowFocus: false,
  });

  const quotationRequest = quotationData?.data?.results?.[0] || null;
  const quotationReference = quotationRequest?.reference || null;

  if (!quotationReference) {
    return null;
  }

  return (
    <>
      <span className="quotation-reference-tag ml-2 inline-flex items-center gap-1">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            isDarkMode
              ? "bg-blue-900/50 text-blue-300 border border-blue-700/50"
              : "bg-blue-100 text-blue-800 border border-blue-200"
          }`}
        >
          {quotationReference}
        </span>
        <Copier content={quotationReference} />
      </span>
      <ReactTooltip
        anchorSelect=".quotation-reference-tag"
        place="top"
        content={
          quotationRequest?.title
            ? `${quotationRequest.title}${quotationRequest.status_display ? ` - Status: ${quotationRequest.status_display}` : ""}`
            : `Quotation Request: ${quotationReference}`
        }
        className={getTooltipClassName(isDarkMode)}
      />
    </>
  );
}
